// CategoryTable.jsx
import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  IconButton,
  Avatar,
  Box,
  Typography,
  Tooltip,
  Menu,
  MenuItem,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { ReactComponent as Link } from '../../../../assets/images/link.svg';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ReactComponent as MoreHoriz } from '../../../../assets/images/menukb.svg';
import NoDataFound from "../../../../components/dashboardcomponents/Traffic/NoDataFound";
import { ReactComponent as VerticalMenu } from '../../../../assets/images/vertical-menu.svg';
import { ReactComponent as Folder } from '../../../../assets/images/categoryFolder.svg';
import { ReactComponent as ArticleFolder } from '../../../../assets/images/ChatImages/folder.svg';
import { ReactComponent as Dropdown } from "../../../../assets/images/dropdownkb.svg";
import { ReactComponent as Eye } from '../../../../assets/images/eye.svg';
import { ReactComponent as Hidden } from '../../../../assets/images/hidden.svg';
import { useDroppable } from '@dnd-kit/core';
import ArticleActionPopup from '../Articles/ArticleActionPopup';
import { useSelector } from 'react-redux';
import { buildExternalKbUrl, selectBusinessSlug } from '../../../../utils/kbPublicUrl';

// Draggable Category Row Component
const CategoryRow = ({
  category,
  index,
  isExpanded,
  categoryBorderBottom,
  toggleExpand,
  handleMenuOpen,
  getVisibilityDisplay,
  isLastCategory,
  lastArticleIndex,
  kbRole,
  businessName,
  onArticleAction,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: category.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <>
      <TableRow
        ref={setNodeRef}
        style={style}
        sx={{
          borderBottom: '1px solid #E5E7EB !important',
          '&:hover': {
            height: '64px',
            backgroundColor: '#F7F8F9',
          },
          backgroundColor: 'transparent',
        }}
      >
        <TableCell sx={{ minWidth: '772px' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',

            }}>
            {/* Drag handle - functional for categories */}
            <Avatar
              sx={{
                width: 24,
                height: 24,
                bgcolor: 'transparent',
                cursor: 'grab',
                '&:active': { cursor: 'grabbing' },
                // Target the SVG directly
                '& svg': {
                  width: '100%',
                  height: '100%',
                  color: '#405261',           // Sets currentColor
                  fill: '#405261',            // Direct fill override
                  '& path': {                 // Target paths inside SVG
                    fill: '#405261',
                  },
                  '& circle': {               // Target circles if any
                    fill: '#405261',
                  }
                },
              }}
              {...attributes}
              {...listeners}
            >
              <VerticalMenu />
            </Avatar>

            {category.articlesList && category.articlesList.length > 0 ? (
              <IconButton
                size="small"
                onClick={() => toggleExpand(category.id)}
                sx={{
                  padding: 0, color: '#405261', '&:hover': {
                    backgroundColor: 'transparent !important',
                  },
                  '&:active': {
                    backgroundColor: 'transparent !important',
                  },
                  '&:focus': {
                    backgroundColor: 'transparent !important',
                  },
                  '&.Mui-focusVisible': {
                    backgroundColor: 'transparent !important',
                  },
                  // Remove the overlay that appears on hover
                  '& .MuiTouchRipple-root': {
                    display: 'none',
                  }
                }}
              >
                <Avatar
                  sx={{
                    width: 24,
                    height: 24,
                    bgcolor: 'transparent',
                    '& svg': {

                      width: '100%',
                      height: '100%',
                      color: '#9CA3AF',
                      transition: 'transform 0.2s ease-in-out',
                      transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                    },
                  }}
                >
                  <Dropdown />
                </Avatar>
              </IconButton>
            ) : null}

            <Avatar sx={{ width: 24, height: 24, bgcolor: 'transparent', '& svg': { color: '#9CA3AF' } }}>
              <Folder width={24} height={24} />
            </Avatar>

            <Typography
              component="div"
              sx={{
                color: '#061522',
                fontFamily: 'Inter',
                fontSize: 14,
                fontStyle: 'normal',
                textTransform: 'capitalize',
                fontWeight: 500,
                lineHeight: '18px',
                display: 'inline-flex',
                alignItems: 'center',
              }}
            >
              {category.title}
              <Typography
                component="span"
                sx={{
                  color: '#405261',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: 14,
                  fontStyle: 'normal',
                  fontWeight: 400,
                  lineHeight: '18px',
                  marginLeft: '8px',
                  display: 'inline',
                }}
              >
                ({category.totalArticles} Articles)
              </Typography>
            </Typography>
          </Box>
        </TableCell>
        <TableCell  >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              height: '24px',
              width: category.visibility === 'public' ? '80px' : '88px',
              borderRadius: '5px',
              border: '1px solid #D5DCE1',
              color: '#405261',
              background: category.visibility === 'public' ? '#FFFFFF' : '#F9FAFA',
              padding: '4px 8px',
            }}
          >
            <Avatar
              sx={{
                width: 16,
                height: 16,
                bgcolor: 'transparent',
                padding: 0,                    // remove any default padding
                '& .MuiSvgIcon-root': {        // target the icon more precisely
                  width: '100%',
                  height: '100%',
                  margin: 0,
                  transform: 'scale(0.8)',
                  transformOrigin: 'center'
                }
              }}
            >
              {category.visibility === 'public' ? <Eye /> : <Hidden />}
            </Avatar>
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 500,
                fontFamily: 'Inter, sans-serif',
              }}
            >
              {getVisibilityDisplay(category.visibility)}
            </Typography>
          </Box>
        </TableCell>
        <TableCell padding='none' sx={{ textAlign: 'right' }} >
          {category?.title?.toLowerCase() !== 'general' && (
            <IconButton
              size="small"
              onClick={(e) => handleMenuOpen(e, category)}
              sx={{ padding: 0, color: '#9CA3AF' }}
            >
              <MoreHoriz sx={{
                fontSize: '24px', color: '#405261'
              }} />
            </IconButton>
          )}
        </TableCell>
      </TableRow >
      {isExpanded &&
        category.articlesList &&
        category.articlesList.length > 0 &&
        category.articlesList.map((article, aIndex) => (
          <SortableSubcategoryRow
            key={article.id}
            article={article}
            parentId={category.id}
            parentTitle={category.title}
            aIndex={aIndex}
            lastArticleIndex={lastArticleIndex}
            isLastCategory={isLastCategory}
            index={index}
            businessName={businessName}
            onArticleAction={onArticleAction}
            kbRole={kbRole}
          />
        ))
      }
    </>
  );
};

// Article Row Component (Non-draggable)
const SortableSubcategoryRow = ({
  article,
  parentId,
  parentTitle,
  aIndex,
  lastArticleIndex,
  isLastCategory,
  index,
  businessName,
  onArticleAction,
  kbRole,
}) => {
  const [copied, setCopied] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [popupType, setPopupType] = useState(null);
  const businessSlug = useSelector(selectBusinessSlug);

  const menuOpen = Boolean(anchorEl);
  const articleId = article._id || article.id;
  const articleStatus = (article.status || '').toLowerCase();
  const articleVisibility = (article.visibility || '').toLowerCase();
  const roleLC = (kbRole || '').toLowerCase();
  const isGeneralCategory =
    (parentTitle || '').trim().toLowerCase() === 'general';

  const articleBorderBottom = '1px solid #E5E7EB !important';

  const handleCopyLink = (e) => {
    e.stopPropagation();
    const url = buildExternalKbUrl({
      slug: businessSlug,
      articleId,
    });
    if (!url) return;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleMenuOpen = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };
  const handleMenuClose = () => setAnchorEl(null);

  const call = (type) => {
    if (onArticleAction) onArticleAction(type, articleId, parentId);
    handleMenuClose();
  };

  const openArchivePopup = () => { setPopupType('archive'); handleMenuClose(); };
  const openRemovePopup = () => { setPopupType('remove'); handleMenuClose(); };
  const closePopup = () => setPopupType(null);
  const handleConfirm = () => {
    if (popupType && onArticleAction) {
      onArticleAction(popupType, articleId, parentId);
    }
    closePopup();
  };

  return (
    <>
      <TableRow
        sx={{
          borderBottom: articleBorderBottom,
          backgroundColor: 'transparent',
          '&:hover': { backgroundColor: '#F7F8F9', height: '64px' },
          '& > *': {
            height: 'auto',
            lineHeight: 'inherit',
          },
        }}
      >

        <TableCell
          colSpan={2}
          sx={{ paddingLeft: '0px !important' }}
        >
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '0px 32px' }}>
            {/* Non-functional icon placeholder for alignment */}
            <Box sx={{ width: 24, height: 24 }} />

            {/* Folder icon — matches Overview.js recent activity */}
            <ArticleFolder />

            {/* Article title */}
            <Typography
              sx={{
                color: '#061522', fontFamily: 'Inter, sans-serif', fontSize: 14,
                fontStyle: 'normal', textTransform: 'capitalize', fontWeight: 400, lineHeight: '18px',
              }}
            >
              {article.title}
            </Typography>

            {/* Copy link — only public + not draft/pending/new */}
            {articleVisibility === 'public' &&
              !['draft', 'pendingreview', 'pending review', 'new'].includes(articleStatus) && (
                <Tooltip title={copied ? 'Copied!' : 'Copy public link'} arrow>
                  <Box
                    component="div"
                    onClick={handleCopyLink}
                    sx={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: 'pointer', '&:hover': { opacity: 0.7 },
                    }}
                  >
                    {copied
                      ? <CheckIcon sx={{ fontSize: 18, color: '#148DFD' }} />
                      : <Link style={{ minWidth: 16, minHeight: 16 }} />
                    }
                  </Box>
                </Tooltip>
              )}
          </Box>
        </TableCell>

        {/* Three-dot menu cell */}
        <TableCell
          padding="none"
          sx={{ textAlign: 'right' }}
        >
          <IconButton
            size="small"
            onClick={handleMenuOpen}
            sx={{ padding: 0 }}
          >
            <MoreHoriz sx={{ fontSize: '24px', color: '#405261' }} />
          </IconButton>
        </TableCell>
      </TableRow>

      {/* Article action menu — identical to ArticlesTable */}
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        elevation={0}
        transitionDuration={0}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        MenuListProps={{ disablePadding: true }}
        PaperProps={{
          sx: {
            width: '184px',
            borderRadius: '8px',
            border: '1px solid #D5DCE1',
            background: '#FFF',
            boxShadow: '0 15px 20px 0 rgba(6, 21, 34, 0.05)',
            mt: '4px',
            zIndex: 1302,
            overflow: 'hidden',
            paddingTop: '8px',
            paddingBottom: '8px',
            '& .MuiMenuItem-root': {
              fontFamily: 'Inter', fontSize: '14px', fontWeight: 400,
              lineHeight: 'normal', px: '16px',
              '&:hover': { borderRadius: '8px', background: '#F9FAFA' },
            },
            '& li:nth-of-type(1)': { color: '#405261 !important' },
            '& li:nth-of-type(2)': { color: '#405261 !important' },
            '& li:nth-of-type(3)': { color: '#405261 !important' },
            '& li:nth-of-type(4)': { color: '#405261 !important' },
            '& li:nth-of-type(5)': { color: '#405261 !important' },
            '& li:nth-of-type(6)': { color: '#FF4D4F !important' },
          },
        }}
      >
        <MenuItem
          onClick={() => call('publish')}
          disabled={articleStatus === 'published' || roleLC === 'viewer'}
        >
          <Typography>Published</Typography>
        </MenuItem>
        <MenuItem
          onClick={() => call('unpublish')}
          disabled={articleStatus !== 'published' || roleLC === 'viewer'}
        >
          <Typography>Unpublished</Typography>
        </MenuItem>
        <MenuItem
          onClick={() => call('public')}
          disabled={articleVisibility === 'public' || roleLC === 'viewer'}
        >
          <Typography>Public</Typography>
        </MenuItem>
        <MenuItem
          onClick={() => call('internal')}
          disabled={articleVisibility === 'internal' || roleLC === 'viewer'}
        >
          <Typography>Internal</Typography>
        </MenuItem>
        <MenuItem
          onClick={openArchivePopup}
          disabled={roleLC === 'contributor' || roleLC === 'viewer'}
        >
          <Typography>Archive</Typography>
        </MenuItem>
        <MenuItem
          onClick={openRemovePopup}
          disabled={
            isGeneralCategory ||
            roleLC === 'contributor' ||
            roleLC === 'viewer'
          }
          sx={{ color: '#FF4D4F !important' }}
        >
          <Typography>Remove</Typography>
        </MenuItem>
      </Menu>

      {/* Archive / Remove from category confirmation popup */}
      {popupType && (
        <ArticleActionPopup
          title={popupType === 'archive' ? 'Move to Archive' : 'Remove from category'}
          description={
            popupType === 'archive'
              ? 'This action will move the article to archive. Continue?'
              : 'This removes the article from this category only. The article will remain in your knowledge base.'
          }
          confirmText={popupType === 'archive' ? 'Move' : 'Remove'}
          cancelText="Cancel"
          onConfirm={handleConfirm}
          onCancel={closePopup}
          popupType={popupType}
        />
      )}
    </>
  );
};
// Main CategoryTable Component
const CategoryTable = ({
  loading,
  visibleCategories,
  expandedIds,
  toggleExpand,
  handleMenuOpen,
  getVisibilityDisplay,
  kbRole,
  allIds,
  isLastCategory,
  getCategoryBorderBottom,
  renderSkeletonRows,
  businessName,
  onArticleAction,
}) => {
  return (
    <TableContainer
      sx={{
        flex: 1,
        marginTop: '0px',
        width: '100%',
        boxShadow: "none",
        marginBottom: visibleCategories.length !== 0 ? '40px' : '0px',
        overflowY: "auto",
      }}
    >
      <Table stickyHeader sx={{
        "& .MuiTableCell-root": {
          paddingLeft: "0px !important",

          height: '64px',
        },
      }}>
        <TableHead sx={{
          "& .MuiTableCell-root": {
            paddingTop: "0px !important",
            paddingBottom: "10px !important",
            zIndex: 1,
            height: "auto",
          },
        }}>
          <TableRow>
            <TableCell
              sx={{
                borderBottom: '1px solid #E5E7EB',
                fontSize: '12px',
                fontWeight: 500,
                minWidth: '500px',
                color: '#405261',
                fontFamily: 'Inter, sans-serif',
              }}
            >
              Title
            </TableCell>
            <TableCell
              sx={{
                borderBottom: '1px solid #E5E7EB',
                fontSize: '12px',
                fontWeight: 500,
                color: '#405261',
                fontFamily: 'Inter, sans-serif',
              }}
            >
              Visibility
            </TableCell>
            <TableCell
              sx={{
                padding: '12px 16px',
                borderBottom: '1px solid #E5E7EB',
                width: 40,
              }}
            />
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            renderSkeletonRows()
          ) : visibleCategories.length === 0 ? (

            <TableRow>
              <TableCell colSpan={2} align="center" sx={{ borderBottom: 'none', }}>
                <NoDataFound
                  containerStyle={{ height: '48vh' }}
                  isForKb={true}
                  title="No categories found"
                  description="This segment is empty. Try another one or come back later"
                />
              </TableCell>
            </TableRow>

          ) : (
            <SortableContext items={allIds} strategy={verticalListSortingStrategy}>
              {visibleCategories.map((category, index) => {
                const isExpanded = expandedIds.includes(category.id);
                const categoryBorderBottom = getCategoryBorderBottom(index, isExpanded);
                const lastArticleIndex = category.articlesList ? category.articlesList.length - 1 : -1;

                return (
                  <CategoryRow
                    key={category.id}
                    category={category}
                    index={index}
                    isExpanded={isExpanded}
                    categoryBorderBottom={categoryBorderBottom}
                    toggleExpand={toggleExpand}
                    handleMenuOpen={handleMenuOpen}
                    getVisibilityDisplay={getVisibilityDisplay}
                    isLastCategory={isLastCategory(index)}
                    lastArticleIndex={lastArticleIndex}
                    kbRole={kbRole}
                    businessName={businessName}
                    onArticleAction={onArticleAction}
                  />
                );
              })}
            </SortableContext>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default CategoryTable;